import { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { Calendar, StatusBadge } from '../../components/nextgen';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useLocal from '../../hooks/useLocal';

import { getWorkOrdersCalendar, updateTask, deleteTask, skipTask } from '../../api';

// Upcoming event item component
const UpcomingEventItem = ({ event, onClick }) => {
	const eventDate = moment(
		event.start || event.scheduledStartDate || event.dueDate,
	);
	const isToday = eventDate.isSame(moment(), 'day');
	const isTomorrow = eventDate.isSame(moment().add(1, 'day'), 'day');

	const dateLabel = isToday
		? 'Today'
		: isTomorrow
			? 'Tomorrow'
			: eventDate.format('ddd, MMM D');

	// Get status color
	const getStatusDot = () => {
		// Tasks are red
		if (event.type === 'task') {
			return 'bg-red-500';
		}
		switch (event.status) {
			case 'in_progress':
				return 'bg-indigo-500';
			case 'scheduled':
				return 'bg-blue-500';
			case 'on_hold':
				return 'bg-orange-500';
			default:
				return 'bg-gray-400';
		}
	};

	const isTask = event.type === 'task';

	return (
		<div
			onClick={onClick}
			className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
		>
			<div className="flex items-start gap-3">
				<div
					className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getStatusDot()}`}
				/>
				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium text-gray-900 truncate">
						{event.title?.includes(':')
							? event.title.split(':').slice(1).join(':').trim()
							: event.title}
					</p>
					<div className="mt-1">
						{isTask ? (
							<span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
								Task
							</span>
						) : (
							<StatusBadge status={event.status} size="xs">
								{dateLabel}
							</StatusBadge>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

// Mobile event list item with full date display
const MobileEventItem = ({ event, onClick, getStatusColor }) => {
	const isTask = event.type === 'task';
	const title = event.title?.includes(':')
		? event.title.split(':').slice(1).join(':').trim()
		: event.title;

	return (
		<div
			onClick={onClick}
			className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
		>
			<div className="flex items-center gap-3">
				<div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(event)}`} />
				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium text-gray-900 truncate">{title}</p>
				</div>
				{isTask ? (
					<span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 flex-shrink-0">
						Task
					</span>
				) : (
					<StatusBadge status={event.status} size="xs" />
				)}
			</div>
		</div>
	);
};

// Mobile month navigation component
const MobileMonthNav = ({ currentMonth, onMonthChange }) => {
	const goToPrevMonth = () => onMonthChange(currentMonth.clone().subtract(1, 'month'));
	const goToNextMonth = () => onMonthChange(currentMonth.clone().add(1, 'month'));
	const goToToday = () => onMonthChange(moment().startOf('month'));

	const isCurrentMonth = currentMonth.isSame(moment(), 'month');

	return (
		<div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 mb-4">
			<button
				onClick={goToPrevMonth}
				className="p-2 hover:bg-gray-100 rounded-md transition-colors"
			>
				<ChevronLeftIcon className="h-5 w-5 text-gray-600" />
			</button>
			<div className="flex items-center gap-3">
				<CalendarIcon className="h-5 w-5 text-gray-400" />
				<span className="text-base font-semibold text-gray-900">
					{currentMonth.format('MMMM YYYY')}
				</span>
				{!isCurrentMonth && (
					<button
						onClick={goToToday}
						className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
					>
						Today
					</button>
				)}
			</div>
			<button
				onClick={goToNextMonth}
				className="p-2 hover:bg-gray-100 rounded-md transition-colors"
			>
				<ChevronRightIcon className="h-5 w-5 text-gray-600" />
			</button>
		</div>
	);
};

const ScheduleCalendar = () => {
	const { get } = useLocal();
	const user = get('user');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openCreateWorkOrder } = useModal('createWorkOrder');
	const { setOpen: openTaskDetail } = useModal('taskDetail');

	const [calendarMonth, setCalendarMonth] = useState(moment().startOf('month'));
	const [hidePast, setHidePast] = useState(true);
	const [filter, setFilter] = useState('all'); // 'all' or 'mine'

	// Calendar data - work orders and tasks (combined endpoint)
	const {
		value: calendarData,
		execute: refreshCalendar,
		status: calendarLoadingStatus,
	} = useAsync(getWorkOrdersCalendar, { immediate: false });

	// Upcoming events data (this week + next week)
	const { value: upcomingData, execute: refreshUpcoming } = useAsync(
		getWorkOrdersCalendar,
		{ immediate: false },
	);

	// Fetch calendar data when month changes (no filters - backend returns all)
	useEffect(() => {
		const startDate = calendarMonth
			.clone()
			.startOf('month')
			.format('YYYY-MM-DD');
		const endDate = calendarMonth.clone().endOf('month').format('YYYY-MM-DD');
		refreshCalendar({ startDate, endDate });
	}, [calendarMonth]);

	// Fetch upcoming events (this week + next week) - no filters, backend returns all
	useEffect(() => {
		const startOfThisWeek = moment().startOf('isoWeek');
		const endOfNextWeek = moment().add(1, 'week').endOf('isoWeek');
		const startDate = startOfThisWeek.format('YYYY-MM-DD');
		const endDate = endOfNextWeek.format('YYYY-MM-DD');
		refreshUpcoming({ startDate, endDate });
	}, []);

	// Split upcoming events into this week and next week, applying filters client-side
	const { thisWeekEvents, nextWeekEvents } = useMemo(() => {
		const allUpcoming = upcomingData || [];
		if (allUpcoming.length === 0)
			return { thisWeekEvents: [], nextWeekEvents: [] };

		const startOfThisWeek = moment().startOf('isoWeek');
		const endOfThisWeek = moment().endOf('isoWeek');
		const startOfNextWeek = moment().add(1, 'week').startOf('isoWeek');
		const endOfNextWeek = moment().add(1, 'week').endOf('isoWeek');
		const now = moment();

		const thisWeek = [];
		const nextWeek = [];

		allUpcoming.forEach((event) => {
			const eventStart = moment(
				event.start || event.scheduledStartDate || event.dueDate,
			);

			// Skip completed/cancelled
			if (['completed', 'cancelled'].includes(event.status)) return;

			// Apply hidePast filter
			if (hidePast && eventStart.isBefore(now, 'day')) return;

			// Apply "mine" filter - check if assigned to current user
			if (filter === 'mine' && user?.id) {
				const isAssignedToMe =
					event.assignedTo === user.id || event.assignedToUser?.id === user.id;
				if (!isAssignedToMe) return;
			}

			if (eventStart.isBetween(startOfThisWeek, endOfThisWeek, 'day', '[]')) {
				thisWeek.push(event);
			} else if (
				eventStart.isBetween(startOfNextWeek, endOfNextWeek, 'day', '[]')
			) {
				nextWeek.push(event);
			}
		});

		// Sort by date
		const sortByDate = (a, b) => {
			const dateA = moment(a.start || a.scheduledStartDate || a.dueDate);
			const dateB = moment(b.start || b.scheduledStartDate || b.dueDate);
			return dateA.diff(dateB);
		};

		return {
			thisWeekEvents: thisWeek.sort(sortByDate),
			nextWeekEvents: nextWeek.sort(sortByDate),
		};
	}, [upcomingData, hidePast, filter, user?.id]);

	// All calendar events (work orders + tasks from combined endpoint)
	const allEvents = calendarData || [];

	// Group events by date for mobile view
	const groupedEventsByDate = useMemo(() => {
		const events = allEvents || [];
		if (events.length === 0) return [];

		const now = moment();
		const grouped = {};

		events.forEach((event) => {
			const eventDate = moment(event.start || event.scheduledStartDate || event.dueDate);
			const dateKey = eventDate.format('YYYY-MM-DD');

			// Apply hidePast filter
			if (hidePast && eventDate.isBefore(now, 'day')) return;

			// Apply "mine" filter
			if (filter === 'mine' && user?.id) {
				const isAssignedToMe =
					event.assignedTo === user.id || event.assignedToUser?.id === user.id;
				if (!isAssignedToMe) return;
			}

			// Skip completed/cancelled
			if (['completed', 'cancelled'].includes(event.status)) return;

			if (!grouped[dateKey]) {
				grouped[dateKey] = {
					date: eventDate,
					dateLabel: eventDate.format('dddd, MMMM D'),
					isToday: eventDate.isSame(now, 'day'),
					isTomorrow: eventDate.isSame(now.clone().add(1, 'day'), 'day'),
					events: [],
				};
			}
			grouped[dateKey].events.push(event);
		});

		// Sort by date and return as array
		return Object.values(grouped)
			.sort((a, b) => a.date.diff(b.date))
			.map((group) => ({
				...group,
				events: group.events.sort((a, b) => {
					const dateA = moment(a.start || a.scheduledStartDate || a.dueDate);
					const dateB = moment(b.start || b.scheduledStartDate || b.dueDate);
					return dateA.diff(dateB);
				}),
			}));
	}, [allEvents, hidePast, filter, user?.id]);

	// Get status dot color for mobile list
	const getStatusDotColor = useCallback((event) => {
		if (event.type === 'task') return 'bg-red-500';
		switch (event.status) {
			case 'in_progress':
				return 'bg-indigo-500';
			case 'scheduled':
				return 'bg-blue-500';
			case 'on_hold':
				return 'bg-orange-500';
			case 'completed':
				return 'bg-green-500';
			default:
				return 'bg-gray-400';
		}
	}, []);

	// Helper to refresh upcoming events
	const doRefreshUpcoming = useCallback(() => {
		const startOfThisWeek = moment().startOf('isoWeek');
		const endOfNextWeek = moment().add(1, 'week').endOf('isoWeek');
		const startDate = startOfThisWeek.format('YYYY-MM-DD');
		const endDate = endOfNextWeek.format('YYYY-MM-DD');
		refreshUpcoming({ startDate, endDate });
	}, [refreshUpcoming]);

	// Helper to refresh all calendar data
	const doRefreshAll = useCallback(() => {
		const startDate = calendarMonth
			.clone()
			.startOf('month')
			.format('YYYY-MM-DD');
		const endDate = calendarMonth.clone().endOf('month').format('YYYY-MM-DD');
		refreshCalendar({ startDate, endDate });
		doRefreshUpcoming();
	}, [calendarMonth, refreshCalendar, doRefreshUpcoming]);

	const handleDayClick = useCallback(
		(date, _events) => {
			// Open create work order modal with the selected date pre-filled
			openCreateWorkOrder({
				initialDate: date.format('YYYY-MM-DD'),
				onSuccess: () => doRefreshAll(),
			});
		},
		[openCreateWorkOrder, doRefreshAll],
	);

	const handleEventClick = useCallback(
		(event) => {
			// Handle task events - open task detail modal
			if (event.type === 'task') {
				openTaskDetail({
					task: {
						id: event.id,
						title: event.title,
						content: event.title,
						dueDate: event.dueDate,
						entityType: event.entityType,
						entityId: event.entityId,
						assignedTo: event.assignedTo,
						assignedToUser: event.assignedToUser,
					},
					onUpdate: async (taskId, updates) => {
						await updateTask(taskId, updates);
						doRefreshAll();
					},
					onToggle: async (taskId, isCompleted) => {
						await updateTask(taskId, { completed: !isCompleted });
						doRefreshAll();
					},
					onDelete: async (taskId) => {
						await deleteTask(taskId);
						doRefreshAll();
					},
					onSkip: async (taskId) => {
						await skipTask(taskId);
						doRefreshAll();
					},
				});
				return;
			}

			// Handle work order events
			if (event.type === 'work_order' || event.workOrderCode) {
				openWorkOrderOverview({
					id: event.id,
					onClose: () => {
						doRefreshAll();
					},
				});
			}
		},
		[openWorkOrderOverview, openTaskDetail, doRefreshAll],
	);

	// Custom color function based on event type and status
	const getEventColor = useCallback((event) => {
		// Task colors - red for tasks with due dates
		if (event.type === 'task') {
			return 'bg-red-100 text-red-800 hover:bg-red-200';
		}

		// Work order colors
		if (event.type === 'work_order' || event.workOrderCode) {
			const hasScheduledDate = event.start || event.scheduledStartDate;
			const hasDueDate = event.dueDate;

			if (event.status === 'completed') {
				return 'bg-green-100 text-green-800 hover:bg-green-200';
			}
			if (event.status === 'in_progress') {
				return 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200';
			}
			if (event.status === 'on_hold') {
				return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
			}
			if (event.status === 'cancelled') {
				return 'bg-red-100 text-red-800 hover:bg-red-200';
			}
			if (!hasScheduledDate && hasDueDate) {
				return 'bg-amber-100 text-amber-800 hover:bg-amber-200';
			}
			if (event.status === 'scheduled') {
				return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
			}
		}

		// Default color
		return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
	}, []);

	// Custom label
	const getEventLabel = useCallback((event) => {
		// Task label - show entity code prefix
		if (event.type === 'task') {
			const prefix = event.entityCode ? `${event.entityCode}: ` : '';
			return `${event.title || 'No title'}`;
		}

		// Work order label
		if (event.type === 'work_order' || event.workOrderCode) {
			const title = event.title?.includes(':')
				? event.title.split(':').slice(1).join(':').trim()
				: event.title;
			return title;
		}
		return event.title;
	}, []);

	const isLoading = calendarLoadingStatus === 'pending';

	return (
		<MainContent maxWidth="w-full">
			<PageHeading
				actions={[
					{
						name: 'Create Work Order',
						onClick: () =>
							openCreateWorkOrder({
								onSuccess: () => doRefreshAll(),
							}),
					},
				]}
				divider={false}
			>
				Calendar
			</PageHeading>

			{/* Filters - mobile friendly */}
			<div className="flex flex-wrap items-center gap-3 mb-4">
				{/* Assignment Filter */}
				<div className="flex items-center rounded-lg border border-gray-200 p-0.5">
					<button
						onClick={() => setFilter('all')}
						className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
							filter === 'all'
								? 'bg-indigo-600 text-white'
								: 'text-gray-600 hover:bg-gray-100'
						}`}
					>
						All
					</button>
					<button
						onClick={() => setFilter('mine')}
						className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
							filter === 'mine'
								? 'bg-indigo-600 text-white'
								: 'text-gray-600 hover:bg-gray-100'
						}`}
					>
						Mine
					</button>
				</div>

				{/* Hide Past Toggle */}
				<label className="flex items-center gap-2 cursor-pointer">
					<input
						type="checkbox"
						checked={hidePast}
						onChange={(e) => setHidePast(e.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
					/>
					<span className="text-xs text-gray-600">Hide past</span>
				</label>
			</div>

			{/* Legend - hidden on mobile */}
			<div className="hidden lg:flex flex-wrap items-center gap-4 text-xs mb-6">
				<span className="text-gray-500 font-medium">Work Orders:</span>
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" />
					<span className="text-gray-600">Scheduled</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded bg-indigo-100 border border-indigo-200" />
					<span className="text-gray-600">In Progress</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded bg-orange-100 border border-orange-200" />
					<span className="text-gray-600">On Hold</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
					<span className="text-gray-600">Completed</span>
				</div>
				<span className="text-gray-300">|</span>
				<span className="text-gray-500 font-medium">Tasks:</span>
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded bg-red-100 border border-red-200" />
					<span className="text-gray-600">Due</span>
				</div>
			</div>

			{/* Mobile View - Events List */}
			<div className="lg:hidden">
				<MobileMonthNav
					currentMonth={calendarMonth}
					onMonthChange={setCalendarMonth}
				/>

				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
					</div>
				) : groupedEventsByDate.length === 0 ? (
					<div className="bg-white border border-gray-200 rounded-lg px-4 py-12 text-center">
						<CalendarIcon className="mx-auto h-12 w-12 text-gray-300" />
						<p className="mt-2 text-sm text-gray-500">
							No events in {calendarMonth.format('MMMM YYYY')}
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{groupedEventsByDate.map((group) => (
							<div
								key={group.date.format('YYYY-MM-DD')}
								className="bg-white border border-gray-200 rounded-lg overflow-hidden"
							>
								<div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
									<h3 className="text-sm font-semibold text-gray-900">
										{group.isToday
											? 'Today'
											: group.isTomorrow
												? 'Tomorrow'
												: group.dateLabel}
									</h3>
								</div>
								<div>
									{group.events.map((event) => (
										<MobileEventItem
											key={`${event.type || 'wo'}-${event.id}`}
											event={event}
											onClick={() => handleEventClick(event)}
											getStatusColor={getStatusDotColor}
										/>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Desktop View - Calendar + Sidebar */}
			<div className="hidden lg:grid grid-cols-12 gap-6">
				<div className="col-span-8">
					<Calendar
						events={allEvents}
						currentMonth={calendarMonth}
						onMonthChange={setCalendarMonth}
						onEventClick={handleEventClick}
						onDayClick={handleDayClick}
						getEventColor={getEventColor}
						getEventLabel={getEventLabel}
						loading={isLoading}
						maxEventsPerDay={4}
					/>
				</div>

				{/* Upcoming Events Sidebar */}
				<div className="col-span-4 space-y-6">
					{/* This Week */}
					<div className="bg-white border border-gray-200 rounded-lg">
						<div className="px-4 py-3 border-b border-gray-200">
							<h3 className="text-sm font-semibold text-gray-900">This Week</h3>
							<p className="text-xs text-gray-500">
								{moment().startOf('isoWeek').format('MMM D')} -{' '}
								{moment().endOf('isoWeek').format('MMM D')}
							</p>
						</div>
						<div className="divide-y divide-gray-100">
							{thisWeekEvents.length === 0 ? (
								<div className="px-4 py-6 text-center text-sm text-gray-500">
									No events this week
								</div>
							) : (
								thisWeekEvents.map((event) => (
									<UpcomingEventItem
										key={`${event.type || 'wo'}-${event.id}`}
										event={event}
										onClick={() => handleEventClick(event)}
									/>
								))
							)}
						</div>
					</div>

					{/* Next Week */}
					<div className="bg-white border border-gray-200 rounded-lg">
						<div className="px-4 py-3 border-b border-gray-200">
							<h3 className="text-sm font-semibold text-gray-900">Next Week</h3>
							<p className="text-xs text-gray-500">
								{moment().add(1, 'week').startOf('isoWeek').format('MMM D')} -{' '}
								{moment().add(1, 'week').endOf('isoWeek').format('MMM D')}
							</p>
						</div>
						<div className="divide-y divide-gray-100">
							{nextWeekEvents.length === 0 ? (
								<div className="px-4 py-6 text-center text-sm text-gray-500">
									No events next week
								</div>
							) : (
								nextWeekEvents.map((event) => (
									<UpcomingEventItem
										key={`${event.type || 'wo'}-${event.id}`}
										event={event}
										onClick={() => handleEventClick(event)}
									/>
								))
							)}
						</div>
					</div>
				</div>
			</div>
		</MainContent>
	);
};

export default ScheduleCalendar;
